import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Result, Spin } from 'antd';
import { useAuthStore } from '@/store/auth.store';
import {
  isOidcTokenValid,
  oidcClaimsToUserInfo,
  parseOidcToken,
  redirectToOidcLogin,
  removeOidcCallbackParams,
} from '@/pages/auth/oidc';

interface OidcCallbackParams {
  accessToken: string | null;
  error: string | null;
}

function readCallbackParams(): OidcCallbackParams {
  const params = new URLSearchParams(window.location.search);
  const accessToken = params.get('code')?.trim() || null;
  const error = params.get('error_description')?.trim() || params.get('error')?.trim() || null;
  return { accessToken, error };
}

export default function OidcCallback() {
  const navigate = useNavigate();
  const { token, setToken, setUser, logout } = useAuthStore();
  const callbackParamsRef = useRef<OidcCallbackParams>(readCallbackParams());
  const startedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!startedRef.current) {
      startedRef.current = true;
      const { accessToken, error: callbackError } = callbackParamsRef.current;
      callbackParamsRef.current = { accessToken: null, error: null };

      window.history.replaceState(
        window.history.state,
        document.title,
        removeOidcCallbackParams(window.location.href)
      );

      if (callbackError) {
        setError(callbackError);
      } else if (!accessToken) {
        if (isOidcTokenValid(token)) {
          navigate('/dashboard', { replace: true });
        } else {
          logout();
          redirectToOidcLogin();
        }
      } else {
        try {
          const { claims, expiresSec } = parseOidcToken(accessToken);
          setToken(accessToken, expiresSec);
          setUser(oidcClaimsToUserInfo(claims));
          navigate('/dashboard', { replace: true });
        } catch (callbackProcessingError) {
          setError(
            callbackProcessingError instanceof Error
              ? callbackProcessingError.message
              : 'Platform Login 失败，请重新登录'
          );
        }
      }
    }
  }, [logout, navigate, setToken, setUser, token]);

  const retryLogin = () => {
    logout();
    redirectToOidcLogin();
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Result
          status="error"
          title="Platform Login 失败"
          subTitle={error}
          extra={
            <Button type="primary" onClick={retryLogin}>
              重新登录
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <Spin size="large" tip="正在完成 Platform Login...">
        <div className="h-20 w-64" />
      </Spin>
    </div>
  );
}
